import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides, mvList } from "@/mocks/mv";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((v) => (v + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const go = (next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  };

  const slide = heroSlides[index];
  const mv = mvList.find((item) => item.id === slide.mvId);

  return (
    <section className="relative w-full overflow-hidden">
      <div
        className="relative h-[440px] w-full md:h-[600px]"
        onTouchStart={(e) => {
          touchStartX.current = e.changedTouches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const delta = e.changedTouches[0].clientX - touchStartX.current;
          if (delta > 50) go(index - 1);
          if (delta < -50) go(index + 1);
          touchStartX.current = null;
        }}
      >
        <img
          key={slide.id}
          src={slide.image}
          alt={`${mv?.title ?? "焦点 MV"} 焦点海报`}
          title={`${mv?.title ?? "焦点 MV"} 焦点海报`}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/70 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40"></div>

        <div className="relative z-10 flex h-full w-full flex-col justify-center px-4 md:px-8">
          <div className="max-w-2xl animate-fade-up">
            <span className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-500/20 px-3 py-1 text-xs font-medium text-primary-300">
              <i className="ri-fire-fill" aria-hidden="true"></i>
              {slide.tagline}
            </span>
            <h1 className="font-heading text-3xl font-bold leading-tight text-foreground-50 md:text-6xl">
              {mv?.title}
            </h1>
            <p className="mt-3 text-base text-foreground-300 md:text-lg">
              {mv?.artist} · {mv?.releaseDate} · {mv?.duration}
            </p>
            <p className="mt-3 hidden max-w-xl text-sm leading-6 text-foreground-400 md:block">
              {mv?.description.slice(0, 60)}…
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/detail/${slide.mvId}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-background-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                立即观看
              </Link>
              <Link
                to="/charts"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-400/40 px-5 py-3 text-sm font-medium text-foreground-100 transition-colors hover:border-foreground-100 hover:bg-background-50/10"
              >
                <i className="ri-bar-chart-2-line text-lg" aria-hidden="true"></i>
                热门榜单
              </Link>
            </div>
          </div>
        </div>

        <button
          type="button"
          aria-label="上一个"
          onClick={() => go(index - 1)}
          className="absolute left-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-foreground-400/30 bg-background-950/50 text-xl text-foreground-100 transition-colors hover:bg-background-950/80 md:left-6"
        >
          <i className="ri-arrow-left-s-line" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          aria-label="下一个"
          onClick={() => go(index + 1)}
          className="absolute right-3 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-foreground-400/30 bg-background-950/50 text-xl text-foreground-100 transition-colors hover:bg-background-950/80 md:right-6"
        >
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
        </button>

        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2">
          {heroSlides.map((item, i) => (
            <button
              key={item.id}
              type="button"
              aria-label={`切换到第 ${i + 1} 张`}
              onClick={() => setIndex(i)}
              className={`h-1.5 cursor-pointer rounded-full transition-all ${
                i === index ? "w-8 bg-accent-500" : "w-3 bg-foreground-500/50"
              }`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}